/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { Form, Table, Icon, Dropdown, Popup, Accordion } from "semantic-ui-react";
import * as _ from "lodash";
import { getDuplicateDriveParameterBindings } from "../../utils/pdApiUtils";

type DriveParameterBindingsProps = {
  domainId: number;
  code: number;
  bindings: Api.DriveParameterBinding[];
  driveVersions: Api.DriveVersions;
  domains: Api.Domains;
  handleBindingsChanged: (bindings: Api.DriveParameterBinding[]) => void;
  permissions: boolean;
  storedbindings: Api.DriveParameter;
  editedbindings: Api.DriveParameter;
};

type DriveParameterBindingsState = {
  isOpen: boolean;
};

/** DriveParameterBindings component */
class DriveParameterBindings extends React.Component<
  DriveParameterBindingsProps,
  DriveParameterBindingsState
> {
  state = { isOpen: false };

  render(): JSX.Element {
    const { bindings, driveVersions, domains, permissions, storedbindings, editedbindings } = this.props;
    const { isOpen } = this.state;
    let versionOptions: any[] = [];
    const duplicateBindings = getDuplicateDriveParameterBindings(bindings);
    const extendedBindings = bindings
      .sort((a, b) => {
        const verA = driveVersions[a?.driveVersion?.id];
        const verB = driveVersions[b?.driveVersion?.id];
        if (a.isDirty || b.isDirty) {
          return 0;
        }
        const tagA = verA?.year + "." + verA?.week + "." + verA?.domainName;
        const tagB = verB?.year + "." + verB?.week + "." + verB?.domainName;
        return tagA > tagB ? -1 : tagA > tagB ? 1 : 0;
      })
      .map((binding, index) => {
        if (duplicateBindings.find(duplicateBinding => duplicateBinding === index) !== undefined) {
          return { ...binding, isDuplicate: true };
        } else {
          return { ...binding, isDuplicate: false };
        }
      });

    editedbindings.driveParameterBindings.forEach((editedBinding: Api.DriveParameterBinding) => {
      if (editedBinding.isDirty) {
        delete editedBinding.isDirty;
      }
    });

    // Creates drive's version dropdown options
    if (isOpen) {
      versionOptions = _.values(driveVersions)
        .map(driveVersion => ({
          key: driveVersion.id,
          text: `${driveVersion.year}.${driveVersion.week}.${
            domains[driveVersion.domain.id].enumName
          }`,
          value: driveVersion.id
        }));
      versionOptions = _.sortBy(versionOptions, ["text"]).reverse();
    }

    let showMandatory = true;
        
    if (storedbindings) {
      showMandatory = !_.isEqual(storedbindings, editedbindings);
    }

    return (
      <Form.Group id="driveParameterBindings" grouped={true}>
        <Form.Field>
          <Accordion fluid={true} styled={true}>
            <Accordion.Title
              active={isOpen}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onClick={() => this.setState(prevState => ({ isOpen: !isOpen }))}
            >
              <Icon name="dropdown" />
              <label>
                <b>Parameter bindings</b>
              </label>
              {duplicateBindings.length >0 && <p className="errorMessagePadding">
              Bindings should not be empty or duplicated.</p>}
            </Accordion.Title>
            <Accordion.Content active={isOpen}>
              <Table compact="very">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Version</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Description</Table.HeaderCell>
                    {!permissions && <Table.HeaderCell>
                      <Popup
                        trigger={
                          <Icon
                            id="addDriveBinding"
                            className="color-green"
                            link={true}
                            name="add"
                            onClick={this.handleAddBinding}
                          />
                        }
                        content={"Add binding"}
                      />
                    </Table.HeaderCell>}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {extendedBindings.map(
                    (binding, index): JSX.Element => (
                      <Table.Row key={index}>
                        <Table.Cell>
                          <div className="flex-row">
                            {binding.isDirty && showMandatory ? "*" : ""}
                            <Dropdown
                              id={`driveBinding-${index}`}
                              compact={true}
                              fluid={true}
                              selection={true}
                              error={binding.isDuplicate}
                              value={binding.driveVersion.id}
                              options={versionOptions}
                              onChange={(e: any, data: any) =>
                                this.handlDriveVersionChange(data.value, index)
                              }
                              disabled={permissions}
                            />
                          </div>
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {driveVersions[binding.driveVersion.id] && (
                            <Popup
                              trigger={
                                <Icon className="color-blue" link={false} name="info circle" />
                              }
                              content={driveVersions[binding.driveVersion.id].description}
                            />
                          )}
                        </Table.Cell>
                        {!permissions && <Table.Cell>
                          <Popup
                            trigger={
                              <Icon
                                className="color-red"
                                link={true}
                                name="remove"
                                onClick={() => this.handleRemoveBinding(index)}
                              />
                            }
                            content={"Remove binding"}
                          />
                        </Table.Cell>}
                      </Table.Row>
                    )
                  )}
                </Table.Body>
              </Table>
            </Accordion.Content>
          </Accordion>
        </Form.Field>
      </Form.Group>
    );
  }

  // Add new binding handler
  private handleAddBinding = (): void => {
    const { code, bindings, handleBindingsChanged } = this.props;
    const newBinding: Api.DriveParameterBinding = {
      driveParameterCode: code,
      driveVersion: {
        id: -1,
        url: ""
      },
      isDirty: true
    };
    handleBindingsChanged([newBinding, ...bindings]);
  };

  // Remove binding handler
  private handleRemoveBinding = (removedIndex: number): void => {
    const { bindings, handleBindingsChanged } = this.props;
    const modifiedBindings = bindings.filter((value, index) => index !== removedIndex);
    handleBindingsChanged(modifiedBindings);
  };

  // Change version handler
  private handlDriveVersionChange = (versionId: number, changedIndex: number): void => {
    const { bindings, driveVersions, handleBindingsChanged } = this.props;
    const driveVersion = driveVersions[versionId];
    if (driveVersion) {
      const editedBindings = bindings.map((binding, index) => {
        if (changedIndex === index) {
          return {
            ...binding,
            driveVersion: { id: driveVersion.id, url: driveVersion.url },
            isDirty: true
          };
        } else {
          return binding;
        }
      });
      handleBindingsChanged(editedBindings);
    }
  };
}

export default DriveParameterBindings;
